// Audience & use-case content — states who each page is for, which use cases
// it supports, and when teams need it (GEO/AEO clarity signals). Rendered by
// components/AudienceUseCase.jsx and baked into the prerendered HTML.

export const AUDIENCE_PAGES = {
  "/": {
    heading: "Who eTrialTrack clinical trial software is for",
    intro:
      "eTrialTrack is built for research organizations that want one compliant system for the full study workflow — here's exactly who uses it, what for, and when it pays off.",
    who: [
      "Pharmaceutical and biotechnology sponsors running Phase I–IV programs",
      "CROs managing multi-sponsor, multi-study portfolios",
      "Research sites, hospitals and investigators digitizing study workflows",
      "Clinical operations and data management teams replacing spreadsheets",
    ],
    cases: [
      "End-to-end study execution: consent, data capture, patient-reported outcomes, randomization and remote visits",
      "Decentralized and hybrid trials with participants beyond the clinic",
      "Multi-site programs that need one real-time operational picture",
      "Inspection-ready studies requiring 21 CFR Part 11, GCP, HIPAA and GDPR alignment",
    ],
    when: [
      "When your team stitches together disconnected tools and loses hours to reconciliation",
      "When an upcoming audit or inspection demands provable data integrity",
      "When study scale grows and manual tracking starts breaking down",
    ],
  },
  "/about-us": {
    heading: "Who we serve as a clinical research technology partner",
    intro:
      "eTrialTrack was founded in April 2024 by a team with 30+ years of EDC experience — we serve the people who run studies, not just the systems around them.",
    who: [
      "Sponsors and CROs who need a responsive, accountable technology partner",
      "Biotech startups running lean teams and aggressive timelines",
      "Academic research institutions and hospitals formalizing digital workflows",
      "Investigators and site staff who need software that follows their day, not the reverse",
    ],
    cases: [
      "First-time EDC adoption with guided onboarding and training",
      "Consolidating consent, data capture and patient engagement into one platform",
      "Long-term partnerships: we refine workflows as your portfolio grows",
    ],
    when: [
      "When you want technology decisions backed by 1M+ patient cases of hands-on experience",
      "When support responsiveness matters — 24/7 across Indian and global time zones",
      "When you need a platform that starts small (one module) and scales with you",
    ],
  },
  "/solutions": {
    heading: "Who our clinical trial solutions are for",
    intro:
      "Six connected capabilities, one platform — adopt the module your study needs today and connect the rest when the workflow demands it.",
    who: [
      "Data management teams (EDC), regulatory and site staff (eConsent)",
      "Outcomes researchers and coordinators (ePRO), supply and randomization teams (RTSM/IWRS)",
      "Decentralized trial teams (TeleHealth), recruitment and retention teams (Participant Tracker)",
      "Sponsors and CROs who want all of the above in one tenant",
    ],
    cases: [
      "Single-module adoption — e.g. start with EDC for one study",
      "Connected workflows: enrollment → consent → capture → outcomes → randomization → remote visits",
      "Portfolio standardization across sponsors, studies and sites",
    ],
    when: [
      "When a new study needs to start in weeks, not months (2–4 week typical deployment)",
      "When per-vendor contracts and integrations are slowing your team down",
      "When leadership wants one audit-ready compliance posture across every module",
    ],
  },
};

const PRODUCT_AUDIENCE = {
  "electronic-data-capture-software-for-clinical-trials": {
    short: "EDC",
    who: [
      "Clinical data managers building and validating eCRFs",
      "CRAs and monitors running review, queries and SDV",
      "Site coordinators entering visit data under time pressure",
      "Biostatistics teams needing clean, export-ready datasets",
    ],
    cases: [
      "Phase I–IV case report form capture with real-time edit checks",
      "Query management and discrepancy resolution across sites",
      "Medical coding and SDV-ready data review workflows",
    ],
    when: [
      "When paper CRFs or spreadsheets create transcription errors and delays",
      "When you need real-time data validation at the point of entry",
      "When an inspection requires a complete, provable audit trail",
    ],
  },
  "electronic-consent-management-system": {
    short: "eConsent",
    who: [
      "Site staff and principal investigators running consent discussions",
      "Regulatory affairs teams tracking consent versions and approvals",
      "Participants who need clear, multilingual consent on any device",
    ],
    cases: [
      "Remote and on-site electronic informed consent with e-signatures",
      "Re-consent workflows when protocols amend mid-study",
      "Version-controlled, audit-ready consent documentation",
    ],
    when: [
      "When ethics committees require strict consent version control",
      "When participants consent remotely in decentralized or hybrid trials",
      "When paper consent creates storage, retrieval and compliance risk",
    ],
  },
  "electronic-patient-reported-outcomes-software": {
    short: "ePRO",
    who: [
      "Outcomes researchers collecting patient-reported endpoints",
      "Study coordinators managing instrument schedules",
      "Participants reporting symptoms and quality of life from home",
    ],
    cases: [
      "BYOD eDiaries and symptom tracking with automated reminders",
      "Validated QoL and efficacy instruments, multilingual delivery",
      "Real-time adherence monitoring and missing-data prevention",
    ],
    when: [
      "When paper diaries come back incomplete or back-filled",
      "When endpoint quality depends on timely, in-the-moment reporting",
      "When you need adherence visibility before the next site visit",
    ],
  },
  "interactive-web-response-system": {
    short: "RTSM/IWRS",
    who: [
      "Randomization and unblinded pharmacy teams",
      "Clinical supply managers tracking investigational product",
      "Study managers overseeing enrollment and treatment arms",
    ],
    cases: [
      "Dynamic randomization and treatment allocation",
      "IP supply, expiry and shipment tracking across depots and sites",
      "Automated resupply triggers and blinded kit management",
    ],
    when: [
      "When adaptive or complex randomization designs outgrow manual lists",
      "When multi-depot supply chains need live stock visibility",
      "When blinding integrity must be system-enforced, not policy-enforced",
    ],
  },
  "video-consultation-module-for-clinical-trials": {
    short: "TeleHealth",
    who: [
      "Sites conducting remote or hybrid study visits",
      "Participants who cannot travel for every assessment",
      "Home-health and nursing teams supporting decentralized trials",
    ],
    cases: [
      "Secure video consultations inside the study workflow",
      "Hybrid visit schedules mixing on-site and remote touchpoints",
      "Remote follow-ups that keep retention high between site visits",
    ],
    when: [
      "When travel burden is your top dropout driver",
      "When protocols add decentralized elements mid-study",
      "When participants are spread across cities or countries",
    ],
  },
  "clinical-trial-participant-tracker-software": {
    short: "Participant Tracker",
    who: [
      "Recruitment and retention teams watching enrollment funnels",
      "Coordinators managing screening-to-completion journeys",
      "Sponsors needing live enrollment and dropout-risk visibility",
    ],
    cases: [
      "End-to-end participant journey tracking with adherence signals",
      "Visit schedules, stipends and milestone management",
      "Early dropout-risk flags so teams intervene in time",
    ],
    when: [
      "When long studies lose participants between touchpoints",
      "When enrollment status lives in spreadsheets and phone calls",
      "When sponsors ask for real-time recruitment dashboards",
    ],
  },
};

const SOLUTION_PAGES_AUD = Object.keys(PRODUCT_AUDIENCE);

export const productAudience = (slug) => {
  const p = PRODUCT_AUDIENCE[slug];
  if (!p) return null;
  return {
    heading: `Who eTrialTrack ${p.short} is for`,
    intro: `The ${p.short} module serves specific roles in a study team — here's who uses it, the use cases it supports, and when your study needs it.`,
    who: p.who,
    cases: p.cases,
    when: p.when,
  };
};

// Location pages: same module-specific content, framed for the city.
export const locationAudience = (city, productSlug) => {
  const p = PRODUCT_AUDIENCE[productSlug] || PRODUCT_AUDIENCE["electronic-data-capture-software-for-clinical-trials"];
  return {
    heading: `Who uses eTrialTrack ${p.short} in ${city}`,
    intro: `For research teams in ${city}, the ${p.short} module fits specific roles and study situations — here's who it serves, what it supports, and when to adopt it.`,
    who: [
      `Pharmaceutical companies and CROs operating in ${city}`,
      `Investigator sites and hospitals across ${city}'s research ecosystem`,
      ...p.who.slice(0, 2),
    ],
    cases: p.cases,
    when: [
      ...p.when.slice(0, 2),
      `When you want local implementation support — onboarding and training aligned to IST, wherever your sites are`,
    ],
  };
};
